const DISMISS_KEY = 'makhe.install.dismissed';

export function isStandaloneApp(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    ('standalone' in window.navigator && Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone))
  );
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isInstallDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissInstallPrompt(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* private browsing */
  }
}

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredInstall: BeforeInstallPromptEvent | null = null;
const installReadyListeners = new Set<() => void>();

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredInstall;
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  installReadyListeners.add(listener);
  return () => installReadyListeners.delete(listener);
}

/** Call once at app boot to capture Chrome/Android install prompt. */
export function initInstallPromptCapture(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstall = e as BeforeInstallPromptEvent;
    installReadyListeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferredInstall = null;
    installReadyListeners.forEach((l) => l());
  });
}

export async function promptNativeInstall(): Promise<boolean> {
  const prompt = deferredInstall;
  if (!prompt) return false;
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  if (outcome === 'accepted') deferredInstall = null;
  return outcome === 'accepted';
}

export function shouldShowInstallPrompt(): boolean {
  if (typeof window === 'undefined') return false;
  if (isStandaloneApp() || isInstallDismissed()) return false;
  return isIosDevice() || Boolean(deferredInstall);
}
